import { useCallback, useEffect, useMemo, useState } from 'react'
import { AuditLogDto, IntentionDto, MAX_INTENTIONS } from '@ivy-monorepo/shared'
import { adminService } from '../../services/admin.service'
import Table from '../../components/Table'
import Pagination from '../../components/Pagination'
import IntentionModal from '../../components/modals/IntentionModal'
import ConfirmationModal from '../../components/modals/ConfirmationModal'
import IntentionsSortableTable from '../../components/admin/IntentionsSortableTable'

const IntentionsPage = () => {
  const [intentions, setIntentions] = useState<IntentionDto[]>([])
  const [auditLogs, setAuditLogs] = useState<AuditLogDto[]>([])
  const [auditPagination, setAuditPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [editingIntention, setEditingIntention] = useState<IntentionDto | null>(null)
  const [deletingIntention, setDeletingIntention] = useState<IntentionDto | null>(null)
  const [isReordering, setIsReordering] = useState(false)
  const [togglingPublicId, setTogglingPublicId] = useState<string | null>(null)

  const activeCount = useMemo(() => intentions.filter(i => i.isActive !== false).length, [intentions])

  const loadIntentions = useCallback(async () => {
    try {
      const response = await adminService.getIntentions()
      setIntentions(response)
    } catch {
      setError('Failed to load intentions.')
    }
  }, [])

  const loadAuditLogs = useCallback(async (page = 1) => {
    try {
      const response = await adminService.getIntentionAuditLogs(page, 10)
      setAuditLogs(response.auditLogs)
      setAuditPagination(response.pagination)
    } catch {
      setError('Failed to load intention audit logs.')
    }
  }, [])

  useEffect(() => {
    const run = async () => {
      setLoading(true)
      await Promise.all([loadIntentions(), loadAuditLogs()])
      setLoading(false)
    }
    run()
  }, [loadIntentions, loadAuditLogs])

  const handleIntentionSaved = (saved: IntentionDto) => {
    setIntentions(prev => {
      const exists = prev.some(item => item.publicId === saved.publicId)
      if (exists) {
        return prev.map(item => (item.publicId === saved.publicId ? saved : item))
      }
      return [...prev, saved].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    })
    loadAuditLogs(auditPagination.page)
  }

  const handleDelete = async () => {
    if (!deletingIntention) return
    try {
      await adminService.deleteIntention(deletingIntention.publicId)
      setIntentions(prev => prev.filter(item => item.publicId !== deletingIntention.publicId))
      setDeletingIntention(null)
      loadAuditLogs(auditPagination.page)
    } catch {
      setError(`Failed to delete intention "${deletingIntention.name}".`)
      setDeletingIntention(null)
    }
  }

  const handleAuditPageChange = (page: number) => {
    loadAuditLogs(page)
  }

  const handleActiveChange = async (intention: IntentionDto, isActive: boolean) => {
    const previous = intentions
    setTogglingPublicId(intention.publicId)
    setError('')
    setIntentions(prev =>
      prev.map(item => (item.publicId === intention.publicId ? { ...item, isActive } : item)),
    )

    try {
      const saved = await adminService.updateIntention(intention.publicId, { isActive })
      handleIntentionSaved(saved)
    } catch {
      setIntentions(previous)
      setError(`Failed to ${isActive ? 'activate' : 'deactivate'} intention "${intention.name}".`)
    } finally {
      setTogglingPublicId(null)
    }
  }

  const handleReorder = async (nextIntentions: IntentionDto[]) => {
    const previous = intentions
    setIntentions(nextIntentions)
    setIsReordering(true)
    setError('')

    try {
      const saved = await adminService.reorderIntentions(nextIntentions.map(intention => intention.publicId))
      setIntentions(saved)
      loadAuditLogs(auditPagination.page)
    } catch {
      setIntentions(previous)
      setError('Failed to save intention order.')
    } finally {
      setIsReordering(false)
    }
  }

  if (loading) {
    return <></>
  }

  return (
    <div className="roles-container">
      <div className="roles-header">
        <h2>Intentions</h2>
      </div>
      <p className="roles-page-intro">
        Manage the venue intentions dictionary (max {MAX_INTENTIONS} active). Drag rows to change display order.
        Users can pick one while checked in; it is cleared on checkout.
      </p>

      {error && <div className="error-message">{error}</div>}

      <div className="roles-actions">
        <button
          type="button"
          onClick={() => setIsCreateModalOpen(true)}
          className="styled-button sm"
          disabled={activeCount >= MAX_INTENTIONS}
        >
          <span>Add intention</span>
        </button>
      </div>

      <div className="roles-table-container">
        <div className="table-wrapper feature-flags-page">
          <IntentionsSortableTable
            intentions={intentions}
            activeCount={activeCount}
            isSaving={isReordering}
            togglingPublicId={togglingPublicId}
            onReorder={handleReorder}
            onRowClick={intention => setEditingIntention(intention)}
            onActiveChange={handleActiveChange}
          />
        </div>
      </div>

      <div className="users-header" style={{ marginTop: '2rem' }}>
        <h3>Intention audit log</h3>
        <Pagination
          currentPage={auditPagination.page}
          totalPages={auditPagination.totalPages}
          onPageChange={handleAuditPageChange}
        />
      </div>

      <div className="users-table-container">
        <div className="table-wrapper">
          <Table
            id="intention-audit-logs"
            name="Intention audit logs"
            keyField="publicId"
            initialSortColumn="createdAt"
            initialSortOrder="desc"
            columns={[
              { label: 'Created', accessor: 'createdAt', type: 'datetime' },
              { label: 'Event', accessor: 'eventType', type: 'enum', enumName: 'AuditLogEventType' },
              { label: 'Producer', accessor: 'producer', type: 'enum', enumName: 'AuditLogProducer' },
              { label: 'Performed by', accessor: 'performedByFullName', type: 'text' as any },
              { label: 'Affected user', accessor: 'affectedUserFullName', type: 'text' as any },
              { label: 'Comment', accessor: 'comment', type: 'text' as any },
            ]}
            sourceData={auditLogs}
            isPropertyBarVisible={false}
            filterTerms=""
            disableGlobalSearch={true}
            disableClientSort={true}
          />
        </div>
      </div>

      <IntentionModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        intention={null}
        activeCount={activeCount}
        onSaved={handleIntentionSaved}
      />

      <IntentionModal
        isOpen={editingIntention !== null}
        onClose={() => setEditingIntention(null)}
        intention={editingIntention}
        activeCount={activeCount}
        onSaved={saved => {
          handleIntentionSaved(saved)
          setEditingIntention(null)
        }}
      />

      {editingIntention && (
        <div className="roles-actions" style={{ marginTop: '1rem' }}>
          <button type="button" className="styled-button sm danger" onClick={() => setDeletingIntention(editingIntention)}>
            Delete intention
          </button>
        </div>
      )}

      <ConfirmationModal
        isOpen={deletingIntention !== null}
        onClose={() => setDeletingIntention(null)}
        onConfirm={handleDelete}
        title="Delete intention"
      >
        {deletingIntention
          ? `Delete "${deletingIntention.name}"? This cannot be undone if users currently have it set.`
          : ''}
      </ConfirmationModal>
    </div>
  )
}

export default IntentionsPage
